import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Typography,
  IconButton,
  Select,
  MenuItem,
  Divider,
  styled,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

// Serializers
import ProfiledDivisionHasUserSerializer from 'issara-sdk/types/ProfiledDivisionHasUserSerializer_apps_PRX'

// Utils
import debounce from "lodash/debounce";

// Types
import {
  ShareMemberSerializer,
  ShareProjectPayload,
  ShareRole,
  ShareResource
} from "./types/MeGenTypes";

type ModShareProjectProps = {
  open: boolean;
  resourceType?: ShareResource;
  projectId: number;
  projectName: string;
  seriesId?: number | null;
  seriesName?: string;
  runMapId?: string | null;
  runMapName?: string;
  /** Current shared members including owner */
  members?: ShareMemberSerializer[];
  /** Loading state for search */
  isSearching?: boolean;
  /** Search results from API */
  searchResults?: ProfiledDivisionHasUserSerializer[];
  /** All OA Users List for Map */
  allOaUsersList?: ProfiledDivisionHasUserSerializer[];
  onClose: () => void;
  onSearchUser?: (query: string) => void;
  onSave?: (payload: ShareProjectPayload) => void;
  onRemoveMembers?: (userIds: string[]) => void;
};

type PendingMember = {
  user_id: number;
  user_name: string;
  role: ShareRole;
};

// Styled Components
const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    width: "800px",
    maxWidth: "90vw",
    padding: "8px",
  },
});

const StyledDialogTitle = styled(DialogTitle)({
  fontSize: "1rem",
  fontWeight: 600,
  color: "#333",
  padding: "16px 20px 8px",
});

const TitleDivider = styled(Divider)({
  borderColor: "#F48FB1",
  borderWidth: "1.5px",
  margin: "0 20px",
});

const SearchField = styled(TextField)({
  margin: "16px 20px 8px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    "& fieldset": {
      borderColor: "#E0E0E0",
    },
    "&:hover fieldset": {
      borderColor: "#BDBDBD",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#E91E63",
    },
  },
});

const MemberRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 20px",
  margin: "0 20px",
  "&:hover": {
    backgroundColor: "#FAFAFA",
  },
});

const MemberName = styled(Typography)({
  fontSize: "0.9rem",
  fontWeight: 500,
  color: "#333",
});

const RoleChip = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "6px 20px",
  borderRadius: "6px",
  fontSize: "0.875rem",
  fontWeight: 500,
  backgroundColor: "whitesmoke",
  color: "#333",
  border: "1px solid #E0E0E0",
  minWidth: "100px",
  textAlign: "center",
});

const RoleSelect = styled(Select<ShareRole>)({
  minWidth: "120px",
  fontSize: "0.875rem",
  borderRadius: "6px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E0E0E0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#BDBDBD",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E91E63",
  },
  "& .MuiSelect-select": {
    padding: "8px 14px",
  },
});

const SearchResultItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 20px",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "#FFF0F3",
  },
});

const ActionButton = styled(Button)({
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 500,
  padding: "8px 24px",
  fontSize: "0.875rem",
});

const CloseButton = styled(ActionButton)({
  backgroundColor: "#757575",
  color: "white",
  "&:hover": {
    backgroundColor: "#616161",
  },
});

const SaveButton = styled(ActionButton)({
  backgroundColor: "#F48FB1",
  color: "white",
  "&:hover": {
    backgroundColor: "#EC407A",
  },
});

const RemoveButton = styled(IconButton)({
  padding: 4,
  color: "#ef4444",
  "&:hover": {
    backgroundColor: "#fef2f2",
  },
});

const DeleteButton = styled(ActionButton)({
  backgroundColor: "#ef4444",
  color: "white",
  "&:hover": {
    backgroundColor: "#dc2626",
  },
});

const SectionLabel = styled(Typography)({
  padding: "8px 20px 4px",
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const StyledCheckbox = styled(Checkbox)({
  padding: "4px",
  color: "#e0e0e0",
  "&.Mui-checked": {
    color: "#ef4444"
  },
});

const ModShareProject: React.FC<ModShareProjectProps> = (props) => {
  const resourceType = props.resourceType ?? "project";
  const seriesId = props.seriesId ?? null;
  const runMapId = props.runMapId ?? null;
  const members = props.members ?? [];
  const isSearching = props.isSearching ?? false;
  const searchResults = props.searchResults ?? [];
  const allOaUsersList = props.allOaUsersList ?? [];

  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Pending List
  const [pendingMembers, setPendingMembers] = useState<PendingMember[]>([]);

  // Role Changes For Existing Members
  const [existingRoleChanges, setExistRoleChanges] = useState<Record<string, ShareRole>>({});

  // Checkbox: Remove From Existing Members
  const [selectedForRemoval, setSelectedForRemoval] = useState<string[]>([]);

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      props.onSearchUser?.(query);
    }, 500),
    [props.onSearchUser]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim().length > 0) {
      setShowSearchResults(true);
      debouncedSearch(query.trim());
    } else {
      setShowSearchResults(false);
      debouncedSearch.cancel();
    }
  };

  const handleSelectUser = (user: ProfiledDivisionHasUserSerializer) => {
    const userId = user.user;

    const alreadyPending = pendingMembers.some(p => p.user_id === userId);
    const alreadyMember = members.some(m => Number(m.user_id) === userId);

    if (!alreadyPending && !alreadyMember) {
      setPendingMembers(prev => [
        ...prev,
        {
          user_id: userId,
          user_name: user.user_profile?.fullname || "",
          role: "viewer",
        },
      ]);
    }

    setSearchQuery("");
    setShowSearchResults(false);
    debouncedSearch.cancel();
  };

  const handlePendingRoleChange = (userId: number, newRole: ShareRole) => {
    setPendingMembers(prev => prev.map(m => (m.user_id === userId ? { ...m, role: newRole } : m)));
  };

  const handleRemovePending = (userId: number) => {
    setPendingMembers(prev => prev.filter(m => m.user_id !== userId));
  };

  const handleExistingRoleChange = (userId: string, newRole: ShareRole) => {
    setExistRoleChanges(prev => ({ ...prev, [userId]: newRole }));
  };

  const handleToggleRemoval = (userId: string) => {
    setSelectedForRemoval(prev =>
      prev.includes(String(userId))
        ? prev.filter(id => id !== String(userId))
        : [...prev, String(userId)]
    );
  };

  const handleSelectAllForRemoval = () => {
    if (selectedForRemoval.length === members.length) {
      // Deselect all
      setSelectedForRemoval([]);
    } else {
      setSelectedForRemoval(members.map(m => m.user_id));
    }
  };

  const handleRemoveSelected = () => {
    if (selectedForRemoval.length === 0) return;

    props.onRemoveMembers?.(selectedForRemoval);
    setSelectedForRemoval([]);
  };

  const handleClose = () => {
    setSearchQuery("");
    setShowSearchResults(false);
    setPendingMembers([]);
    setExistRoleChanges({});
    setSelectedForRemoval([]);
    debouncedSearch.cancel();
    props.onClose();
  };

  const handleSave = () => {
    const usersToSave: ShareMemberSerializer[] = [];

    // 1. Pending members (new)
    pendingMembers.forEach(pm => {
      usersToSave.push({
        user_id: String(pm.user_id),
        role: pm.role,
      });
    });

    // 2. Existing members (changes role)
    Object.entries(existingRoleChanges).forEach(([uid, role]) => {
      usersToSave.push({
        user_id: uid,
        role,
      });
    });

    // API Save
    if (usersToSave.length > 0) {
      props.onSave?.({
        resource_type: resourceType,
        project_id: props.projectId,
        series_id: resourceType === "series" ? (seriesId ?? null) : null,
        thread_id: resourceType === "runmap" ? (runMapId ?? null) : null,
        users: usersToSave,
      });
    }
    
    handleClose();
  };

  const dialogTitle = (type: ShareResource) => {
    switch (type) {
      case "runmap":
        return `Share Run: ${props.runMapName}`;
      case "series":
        return `Share Series: ${props.seriesName}`;
      case "project":
        return `Share Project: ${props.projectName}`;
    }
  };

  // Filter out already-added members from search results
  const memberUserIds = new Set(members.map((m) => m.user_id));
  const pendingUserIds = new Set(pendingMembers.map(p => p.user_id));
  const filteredSearchResults = searchResults.filter(
    (r) => {
      const uid = String(r.id);

      return !memberUserIds.has(uid) && !pendingUserIds.has(Number(uid));
    }
  );
  const hasChanges = pendingMembers.length > 0 || Object.keys(existingRoleChanges).length > 0;
  const hasSelectedForRemoval = selectedForRemoval.length > 0;
  const isAllSelected = members.length > 0 && selectedForRemoval.length === members.length;

  return (
    <StyledDialog id="megen-mod-share-container" open={props.open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* Title */}
      <StyledDialogTitle id="megen-mod-share-header">
        {/* Share Project : &quot;{props.projectName}&quot; */}
        {dialogTitle(resourceType)}
      </StyledDialogTitle>

      <TitleDivider />

      {/* Search Field */}
      <SearchField
        id="megen-mod-share-search-input"
        placeholder="เพิ่มสิทธิ์การเข้าถึงด้วยการค้นหาชื่อ-นามสกุล"
        value={searchQuery}
        onChange={handleSearchChange}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {isSearching ? (
                <CircularProgress size={20} />
              ) : (
                <SearchIcon sx={{ color: "#999" }} />
              )}
            </InputAdornment>
          ),
        }}
      />

      {/* Search Results Dropdown */}
      {showSearchResults && filteredSearchResults.length > 0 && (
        <Box
          id="megen-mod-share-search-results"
          sx={{
            mx: "20px",
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
            maxHeight: "160px",
            overflowY: "auto",
            backgroundColor: "white",
            zIndex: 10,
            position: "relative",
          }}
        >
          {filteredSearchResults.map((user) => (
            <SearchResultItem
              id={`megen-mod-share-search-result-${user.id}`}
              key={user.id}
              onClick={() => handleSelectUser(user)}
            >
              <Typography sx={{ fontSize: "0.875rem", color: "#333" }}>
                {user.user_profile?.fullname}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>
                + เพิ่ม
              </Typography>
            </SearchResultItem>
          ))}
        </Box>
      )}

      {/* Content */}
      <DialogContent id="megen-mod-share-content" sx={{ padding: "8px 0", minHeight: "200px" }}>
        {/* Pending Members (New) */}
        {pendingMembers.length > 0 && (
          <>
            <SectionLabel>เพิ่มใหม่</SectionLabel>
            {pendingMembers.map((pm) => (
              <MemberRow
                id={`megen-mod-share-pending-member-${pm.user_id}`}
                key={`pending-${pm.user_id}`}
                sx={{ backgroundColor: "#FFF9FB" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <RemoveButton
                    id={`megen-mod-share-pending-remove-btn-${pm.user_id}`}
                    size="small"
                    onClick={() => handleRemovePending(pm.user_id)}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </RemoveButton>
                  <MemberName>{pm.user_name}</MemberName>
                </Box>

                <RoleSelect
                  value={pm.role}
                  onChange={(e) =>
                    handlePendingRoleChange(
                      pm.user_id,
                      e.target.value as ShareRole
                    )
                  }
                  size="small"
                >
                  <MenuItem value="viewer">Viewer</MenuItem>
                  <MenuItem value="editor">Editor</MenuItem>
                </RoleSelect>
              </MemberRow>
            ))}

            {members.length > 0 && <Divider sx={{ my: 1, mx: "20px" }} />}
          </>
        )}

        {/* Existing Members (Saved) */}
        {members.length > 0 && (
          <>
            {/* Header Section: Select All + Delete Button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: "20px",
                pt: pendingMembers.length > 0 ? "0" : "8px",
                marginBottom: "8px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <StyledCheckbox
                  size="small"
                  checked={isAllSelected}
                  indeterminate={hasSelectedForRemoval && !isAllSelected}
                  onChange={handleSelectAllForRemoval}
                />
                <SectionLabel sx={{ padding: 0 }}>
                  สมาชิกปัจจุบัน ({members.length})
                </SectionLabel>
              </Box>

              {hasSelectedForRemoval && (
                <DeleteButton
                  id="megen-mod-share-delete-selected-btn"
                  size="small"
                  startIcon={<DeleteOutlineIcon sx={{ fontSize: "1rem !important" }} />}
                  onClick={handleRemoveSelected}
                >
                  ลบ ({selectedForRemoval.length})
                </DeleteButton>
              )}
            </Box>

            {members.map((member) => {
              const uid = member.user_id;
              const isChecked = selectedForRemoval.includes(uid);
              const userFullName = (allOaUsersList).find(user => user.user === Number(member.user_id))?.user_profile?.fullname;

              return (
                <MemberRow
                  id={`megen-mod-share-member-${member.user_id}`}
                  key={`existing-${member.user_id}`}
                  sx={{
                    backgroundColor: isChecked ? "#fef2f2" : "transparent",
                    transition: "background-color 0.15s ease",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <StyledCheckbox
                      size="small"
                      checked={isChecked}
                      onChange={() => handleToggleRemoval(uid)}
                    />
                    <MemberName
                      sx={{
                        textDecoration: isChecked ? "line-through" : "none",
                        color: isChecked ? "#999" : "#333",
                      }}
                    >
                      {userFullName || `User ID: ${member.user_id}`}
                    </MemberName>
                  </Box>

                  <RoleSelect
                    value={existingRoleChanges[uid] || member.role}
                    onChange={(e) =>
                      handleExistingRoleChange(
                        uid,
                        e.target.value as ShareRole
                      )
                    }
                    size="small"
                    disabled={isChecked}
                  >
                    <MenuItem value="viewer">Viewer</MenuItem>
                    <MenuItem value="editor">Editor</MenuItem>
                  </RoleSelect>
                </MemberRow>
              );
            })}
          </>
        )}

        {/* Empty State */}
        {members.length === 0 && pendingMembers.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4, color: "#999" }}>
            <Typography sx={{ fontSize: "0.875rem" }}>
              ยังไม่มีผู้ร่วมใช้งาน
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions id="megen-mod-share-footer" sx={{ padding: "12px 20px", gap: 1 }}>
        <CloseButton id="megen-mod-share-close-btn" onClick={handleClose}>ปิดหน้าจอ</CloseButton>
        <SaveButton id="megen-mod-share-save-btn" onClick={handleSave}>บันทึกข้อมูล</SaveButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default ModShareProject;
